import React, { useState, useEffect } from 'react';
import { Badge, Avatar, Tabs, List, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSecureService } from '../../store/service';
import { useAtom } from 'jotai';
import { userAtom } from '../../store/atoms';
import styles from '../../layouts/mainLayout.module.scss';

interface NotificationDropdownProps {
  isMobile: boolean;
  onMobileDrawerClose?: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isMobile,
  onMobileDrawerClose
}) => {
  const [user, setUser] = useAtom(userAtom);
  const navigate = useNavigate();
  const { getUserNotifications, changeNotificationStatus } = useSecureService();
  
  // Notification states
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<any[]>([]);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const [activeNotificationTab, setActiveNotificationTab] = useState('all');
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Notification functions
  const loadNotifications = async (unreadOnly = false) => {
    if (!user) return;
    
    setLoadingNotifications(true);
    try {
      const response = await getUserNotifications();
      
      if (response.success && response.data) {
        // API'den gelen veri yapısı: { data: { notifications: [...] } }
        const allNotifications = response.data.notifications || response.data;
        const unread = allNotifications.filter((n: any) => !n.isRead);
        
        setNotifications(allNotifications);
        setUnreadNotifications(unread);
      } else {
        setNotifications([]);
        setUnreadNotifications([]);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleNotificationClick = () => {
    setIsNotificationDropdownOpen(!isNotificationDropdownOpen);
    if (!isNotificationDropdownOpen) {
      loadNotifications();
    }
  };

  const formatNotificationTime = (dateString: string) => {
    // API'den gelen tarih GMT+0 (UTC) formatında
    // Eğer 'Z' yoksa UTC olduğunu belirtmek için ekleyelim
    const utcDateString = dateString.includes('Z') ? dateString : dateString + 'Z';
    
    // UTC tarihi parse et
    const utcDate = new Date(utcDateString);
    const now = new Date();
    
    // Eğer tarih geçersizse
    if (isNaN(utcDate.getTime())) {
      return 'Unknown time';
    }
    
    // Fark hesaplama
    const diffInMinutes = Math.floor((now.getTime() - utcDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hr ago`;
    } else if (diffInMinutes < 10080) { // 7 gün
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      // Tarayıcının yerel saatine göre format
      return utcDate.toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
  };

  const formatNotificationText = (notification: any) => {
    if (!notification.notificationText) return 'Notification';
    
    let text = notification.notificationText;
    
    // Template değişkenlerini parameters ile değiştir
    if (notification.parameters && Array.isArray(notification.parameters)) {
      notification.parameters.forEach((param: any, index: number) => {
        const placeholder = `{{${param.key}}}`;
        text = text.replace(placeholder, param.value || '');
      });
    }
    
    return text;
  };

  const handleNotificationItemClick = async (notification: any) => {
    // Eğer notification henüz okunmamışsa, okundu olarak işaretle
    if (!notification.isRead) {
      try {
        const response = await changeNotificationStatus([notification.id], true);
        if (response.success) {
          // Local state'i güncelle
          setNotifications(prev => 
            prev.map(n => 
              n.id === notification.id ? { ...n, isRead: true } : n
            )
          );
          setUnreadNotifications(prev => 
            prev.filter(n => n.id !== notification.id)
          );
          
          // User'ın unread count'unu güncelle
          if (user) {
            setUser({
              ...user,
              unreadNotificationCount: Math.max(0, (user.unreadNotificationCount || 0) - 1)
            });
          }
        }
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
    
    // Notification target'ına yönlendir
    if (notification.notificationTarget) {
      // Dropdown'ı kapat
      setIsNotificationDropdownOpen(false);
      
      // Mobile drawer'ı kapat
      if (isMobile && onMobileDrawerClose) {
        onMobileDrawerClose();
      }
      
      // Target path'e yönlendir
      navigate(`/${notification.notificationTarget}`);
    }
  };

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Dropdown içindeki herhangi bir element'e tıklandığında kapanmasın
      const dropdownElement = document.querySelector('[class*="notificationDropdown"]');
      const isInsideDropdown = dropdownElement && dropdownElement.contains(target);
      const isAvatar = target.closest('.notification-avatar');
      const isTabs = target.closest('.ant-tabs');
      
      if (!isInsideDropdown && !isAvatar && !isTabs) {
        setIsNotificationDropdownOpen(false);
      }
    };

    if (isNotificationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationDropdownOpen]);

  if (!user) return null;

  return (
    <div style={{ position: 'relative' }}>
      <Badge count={user?.role === 'Guest' ? 0 : (user?.unreadNotificationCount || 0)} showZero={false}>
        <Avatar 
          style={{
            fontWeight: '500',
            fontSize: '16px',
          }}
          className={`notification-avatar ${styles.userAvatar}`}
          onClick={handleNotificationClick}
          src={user?.thumbnailUrl}
          size={isMobile ? 24 : 32}
        >
          {!user?.thumbnailUrl && (
            <>
          {user?.firstName?.[0]?.toUpperCase() || ''}{user?.lastName?.[0]?.toUpperCase() || ''}
            </>
          )}
        </Avatar>
      </Badge>
      
      {/* Notification Dropdown */}
      {isNotificationDropdownOpen && (
        <div 
          className={`${styles.notificationDropdown} notification-dropdown`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.notificationDropdownInner}>
          {/* Tabs */}
          <div className={styles.notificationTabs}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Tabs
                activeKey={activeNotificationTab}
                onChange={setActiveNotificationTab}
                items={[
                  {
                    key: 'unread',
                    label: 'Unread',
                    children: null
                  },
                  {
                    key: 'all',
                    label: 'All',
                    children: null
                  }
                ]}
                style={{
                  color: '#fff'
                }}
              />
              
              {/* Mark All as Read Button */}
              {unreadNotifications.length > 0 && (
                <Button
                  type="text"
                  size="small"
                  className={styles.markAllAsReadButton}
                  onClick={async () => {
                    try {
                      const unreadIds = unreadNotifications.map(n => n.id);
                      const response = await changeNotificationStatus(unreadIds, true);
                      
                      if (response.success) {
                        // Local state'i güncelle
                        setNotifications(prev => 
                          prev.map(n => 
                            unreadIds.includes(n.id) ? { ...n, isRead: true } : n
                          )
                        );
                        setUnreadNotifications([]);
                        
                        // User'ın unread count'unu sıfırla
                        if (user) {
                          setUser({
                            ...user,
                            unreadNotificationCount: 0
                          });
                        }
                      }
                    } catch (error) {
                      console.error('Error marking all notifications as read:', error);
                    }
                  }}
                >
                  Mark All as Read
                </Button>
              )}
            </div>
          </div>
          
          {/* Notification List */}
          <div className={styles.notificationList}>
            <List
              loading={loadingNotifications}
              dataSource={(activeNotificationTab === 'unread' ? unreadNotifications : notifications).slice(0, 4)}
              renderItem={(item: any) => (
                <List.Item 
                  className={styles.notificationItem}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNotificationItemClick(item);
                  }}
                >
                  <div className={styles.notificationItemContent}>
                    {/* Status Dot */}
                    <div 
                      className={styles.statusDot}
                      style={{
                        backgroundColor: item.isRead ? '#666' : '#ff4d4f'
                      }}
                    />
                    
                    {/* Content */}
                    <div className={styles.notificationContent}>
                      <div className={`${styles.notificationTitle} ${item.isRead ? styles.notificationTitleRead : ''}`}>
                        {formatNotificationText(item)}
                      </div>
                      <div className={`${styles.notificationTime} ${item.isRead ? styles.notificationTimeRead : styles.notificationTimeUnread}`}>
                        {formatNotificationTime(item.createdAt)}
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
              locale={{
                emptyText: (
                  <div className={styles.notificationEmpty}>
                    {activeNotificationTab === 'unread' ? 'No unread notifications' : 'No notifications'}
                  </div>
                )
              }}
            />
          </div>
          
          {/* See All Button */}
          <div className={styles.notificationFooter}>
            <Button 
              type="primary" 
              className={styles.notificationButton}
              onClick={() => {
                setIsNotificationDropdownOpen(false);
                navigate('/results?type=notifications');
              }}
            >
              See All
            </Button>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown; 